import classNames from 'classnames';
import Spinner from '@/components/atoms/spinner/spinner';
import useQuerySearchDestinationHierarchy from '@/integrations/content/content-destinations/search-destinations-hierarchy/search-destination-hierarchy.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useDebounce from '@/libraries/hooks/use-debounce';
import SearchBarDestinationGroup from './search-bar-destination-group/search-bar-destination-group';
import styles from './search-bar-locations-search-results.module.scss';

export interface SearchBarLocationsSearchResultsProps {
  /** The campaign ID */
  campaignId?: string;
  /** The class name to apply */
  className?: string;
  /** Departure id */
  departureIds?: string;
  /** An event that is triggered on click */
  onClickDestination?: (
    destination: Content.Components['SearchDestinationNode'],
  ) => void;
  /** Period type */
  periodType?: Content.Components['PeriodType'];
  /** Search string */
  searchString: string;
}
const DEBOUNCE_DELAY = 500;

export const MINIMUM_SEARCH_STRING_LENGTH = 3;

const SearchBarLocationsSearchResults = ({
  campaignId,
  className,
  departureIds,
  onClickDestination,
  periodType,
  searchString,
}: SearchBarLocationsSearchResultsProps) => {
  const { t } = useI18n();
  const { value: debouncedSearchString } = useDebounce(
    searchString,
    DEBOUNCE_DELAY,
  );
  const {
    data: dataSearchDestinationHierarchy,
    isFetching: isFetchingSearchDestinationHierarchy,
  } = useQuerySearchDestinationHierarchy(
    {
      campaignId: campaignId ?? '',
      departureIds: departureIds ?? '',
      pattern: debouncedSearchString ?? '',
      patternMatch: 'all',
      periodType: periodType,
    },
    { enabled: debouncedSearchString.length >= MINIMUM_SEARCH_STRING_LENGTH },
  );

  return (
    <div className={classNames(styles.component, className)}>
      {isFetchingSearchDestinationHierarchy && (
        <Spinner
          className={styles.spinner}
          loadingText={t('items.search.searchBar.updatingSearch')}
        />
      )}

      {dataSearchDestinationHierarchy && (
        <div className={styles.destinationsWrapper}>
          {[
            { key: 'hotel', label: t('items.search.searchBar.location.hotel') },
            {
              key: 'destinationRegion',
              label: t('items.search.searchBar.location.destinationRegion'),
            },
            {
              key: 'destination',
              label: t('items.search.searchBar.location.destination'),
            },
            {
              key: 'country',
              label: t('items.search.searchBar.location.country'),
            },
          ].map(({ key, label }) => {
            const group = dataSearchDestinationHierarchy.result[key];

            return group?.length ? (
              <SearchBarDestinationGroup
                key={key}
                destinationGroup={group}
                groupName={label}
                onClickDestination={onClickDestination}
              />
            ) : null;
          })}
        </div>
      )}
    </div>
  );
};

SearchBarLocationsSearchResults.displayName = 'SearchBarLocationsSearchResults';

export default SearchBarLocationsSearchResults;
